import React, { useState } from 'react'

import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'

import * as Yup from 'yup'
import { Field, Form, Formik, FormikHelpers } from 'formik'

import { Box, Stack, Typography, Link, Button, Alert } from '@mui/material'

import { useMutationPostAuthRegister } from '@/api/auth/mutations'

import { TextInput } from '@/components/Inputs/Text'
import { MaskedInput } from '@/components/Inputs/Masked'
import { PasswordInput } from '@/components/Inputs/Password'
import { NewPasswordInput } from '@/components/Inputs/NewPassword'
import { validatePassword } from '@/components/Inputs/NewPassword/helpers/validatePassword'

import { HttpError } from '@/api/http'

import { useBackButton } from '@/hooks/useBackButton'

export interface RegisterFormData {
  first_name: string
  last_name: string
  cpf: string
  phone: string
  email: string
  password: string
  confirmPassword: string
}

const initialValues: RegisterFormData = {
  first_name: '',
  last_name: '',
  cpf: '',
  phone: '',
  email: '',
  password: '',
  confirmPassword: '',
}

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required('O campo de nome é obrigatório'),
  last_name: Yup.string().required('O campo de sobrenome é obrigatório'),
  cpf: Yup.string()
    .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'O CPF informado é inválido')
    .required('O campo de CPF é obrigatório'),
  phone: Yup.string()
    .trim()
    .matches(/^\+55 \(\d{2}\) \d{5}-\d{4}$/, 'O telefone informado é inválido'),
  email: Yup.string().email('O e-mail informado é inválido').required('O campo de e-mail é obrigatório'),
  password: Yup.string().required('O campo de senha é obrigatório').test({
    name: 'password',
    test: validatePassword,
    message: '',
  }),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), undefined], 'As senhas devem coincidir')
    .required('Confirmação de senha é obrigatória'),
})

const RegisterPage: React.FC = () => {
  useBackButton()

  const navigate = useNavigate()
  const { state } = useLocation()

  const [registerSuccess, setRegisterSuccess] = useState<string | null>(null)
  const [registerError, setRegisterError] = useState<string | null>(null)

  const { mutateAsync: mutatePostAuthRegister } = useMutationPostAuthRegister()

  const handleOnSubmitRegister = (values: RegisterFormData, { setSubmitting }: FormikHelpers<RegisterFormData>) => {
    const { first_name, last_name, cpf, phone, email, password } = values

    mutatePostAuthRegister({
      first_name,
      last_name,
      cpf,
      phone,
      email,
      password,
      redirect_path: '/',
    })
      .then(() => {
        setRegisterSuccess('Cadastro realizado com sucesso! Você será redirecionado para a página de login.')

        setTimeout(() => {
          navigate('/auth/login', { state })
        }, 5000)
      })
      .catch((error: HttpError) => {
        setRegisterError(
          error.message || 'Ocorreu um erro inesperado ao tentar fazer o cadastro! Tente novamente mais tarde.'
        )
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <Stack direction="column" alignItems="center" justifyContent="center" spacing={4}>
      <Stack direction="column" alignItems="center" textAlign="center">
        <Typography variant="h6" fontWeight="bold">
          Criar conta na MicroRED
        </Typography>
        <Typography variant="subtitle1">Carregue fácil, Ande mais!</Typography>
      </Stack>

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleOnSubmitRegister}>
        {({ submitForm, isSubmitting }) => (
          <Box component={Form} id="register-form" name="register" width="100%">
            <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
              {registerError ? (
                <Alert severity="error" sx={{ width: '100%', alignItems: 'center', borderRadius: 4 }}>
                  {registerError}
                </Alert>
              ) : null}
              {registerSuccess ? (
                <Alert severity="success" sx={{ width: '100%', alignItems: 'center', borderRadius: 4 }}>
                  {registerSuccess}
                </Alert>
              ) : null}

              <Field component={TextInput} placeholder="Digite seu nome" name="first_name" autoComplete="given-name" />
              <Field
                component={TextInput}
                placeholder="Digite seu sobrenome"
                name="last_name"
                autoComplete="family-name"
              />
              <Field
                component={TextInput}
                placeholder="Digite seu CPF"
                id="cpf"
                name="cpf"
                autoComplete="username"
                InputProps={{
                  inputComponent: MaskedInput,
                  inputProps: {
                    inputMode: 'numeric',
                    mask: '___.___.___-__',
                    replacement: {
                      _: /[0-9]/,
                    },
                  },
                }}
              />
              <Field
                component={TextInput}
                placeholder="Digite seu telefone"
                name="phone"
                autoComplete="tel"
                InputProps={{
                  inputComponent: MaskedInput,
                  inputProps: {
                    inputMode: 'numeric',
                    mask: '+55 (__) _____-____',
                    replacement: {
                      _: /[0-9]/,
                    },
                  },
                }}
              />
              <Field
                component={TextInput}
                placeholder="Digite seu e-mail"
                id="email"
                name="email"
                type="email"
                autoComplete="email"
              />
              <NewPasswordInput name="password" placeholder="Digite sua senha" autoComplete="new-password" />
              <PasswordInput name="confirmPassword" placeholder="Confirme sua senha" autoComplete="new-password" />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ borderRadius: 20 }}
                loading={isSubmitting}
                disableElevation
                fullWidth
              >
                Criar conta
              </Button>

              <Stack alignItems="center" justifyContent="center">
                <Link component={RouterLink} to="/auth/login" state={state} variant="body2" underline="hover">
                  Já tem uma conta? Entrar
                </Link>
              </Stack>
            </Stack>
          </Box>
        )}
      </Formik>
    </Stack>
  )
}

export default RegisterPage
